import { Component, computed, input, model, signal } from '@angular/core';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { Tag } from 'primeng/tag';
import { ContentDocument, RealmConfig } from '@/lib-backend';
import {
    LEGAL_DOC_KEYS,
    LEGAL_DOC_LABELS,
    LegalDocKey,
    LegalOverridesDraft,
} from '@/app/models/legal-documents';
import { OverridableSection } from '@/app/components/category-config/overridable-section';
import { LegalDocView } from '@/app/components/category-config/legal-doc-view';
import { LegalDocEditDialog } from '@/app/components/category-config/legal-doc-edit-dialog';

/**
 * Realm-level legal documents, one sub-tab per document: the tab shows the
 * (draft) title and rendered content; an overridden document is edited in a
 * popup dialog. Edits land in the shared website-info draft and are persisted
 * by the page-level "Save all" (FieldUpdate semantics built in
 * website-info.buildLegalUpdate).
 */
@Component({
    selector: 'app-website-info-legal',
    standalone: true,
    imports: [
        Tabs,
        Tab,
        TabList,
        TabPanel,
        TabPanels,
        Tag,
        OverridableSection,
        LegalDocView,
        LegalDocEditDialog,
    ],
    templateUrl: './legal-tab.html',
})
export class LegalTab {
    readonly realm = input.required<RealmConfig>();
    readonly draft = model.required<LegalOverridesDraft>();

    protected readonly docKeys = LEGAL_DOC_KEYS;
    protected readonly labels = LEGAL_DOC_LABELS;

    private readonly details = computed(
        () => this.realm().CategorySpecific.Legal,
    );

    // ── Edit dialog ──────────────────────────────────────────────────────────
    protected readonly editDialogVisible = signal(false);
    protected readonly editingKey = signal<LegalDocKey | null>(null);
    protected readonly editTitle = signal('');
    protected readonly editContent = signal('');

    protected readonly editDialogHeader = computed(() => {
        const key = this.editingKey();
        return key ? `Edit — ${this.labels[key]}` : '';
    });

    protected docDirty(key: LegalDocKey): boolean {
        const section = this.details()[key];
        const entry = this.draft()[key];
        return (
            entry.overridden !== section.IsOverridden ||
            JSON.stringify(entry.doc) !== JSON.stringify(section.Effective)
        );
    }

    protected onToggle(key: LegalDocKey, overridden: boolean): void {
        const section = this.details()[key];
        this.draft.update((d) => ({
            ...d,
            [key]: {
                overridden,
                // Turning the override off reverts the document to the inherited template.
                doc: overridden ? { ...d[key].doc } : { ...section.Inherited },
            },
        }));
    }

    protected resetDoc(key: LegalDocKey): void {
        const section = this.details()[key];
        this.draft.update((d) => ({
            ...d,
            [key]: {
                overridden: section.IsOverridden,
                doc: { ...section.Effective },
            },
        }));
    }

    protected openEdit(key: LegalDocKey): void {
        const doc = this.draft()[key].doc;
        this.editingKey.set(key);
        this.editTitle.set(doc.Title);
        this.editContent.set(doc.Content);
        this.editDialogVisible.set(true);
    }

    protected applyEdit(): void {
        const key = this.editingKey();
        if (key) {
            this.setDoc(key, {
                Title: this.editTitle(),
                Content: this.editContent(),
            });
        }
        this.editingKey.set(null);
    }

    private setDoc(key: LegalDocKey, patch: Partial<ContentDocument>): void {
        this.draft.update((d) => ({
            ...d,
            [key]: { ...d[key], doc: { ...d[key].doc, ...patch } },
        }));
    }
}
